import { FC, useMemo } from "react";
import { kebabCase } from "lodash-es";

import { SideMenu } from "shared/components";
import { IMenuItemType, ISideMenuItem } from "shared/components/SideMenu/types";
import { useBandContext, useEventContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { buildUrl, getIcon, getIdFromPath, mapUserPermissions } from "shared/utils";
import { Clock, Guests, Hotel, Plane, UserMultiple, Venue } from "shared/assets/icons";
import { API_ENDPOINTS } from "shared/constants";
import { EventUniqueBlocks, IIconBandSetting } from "Bands/types";
import { useSelector } from "react-redux";
import { bandSettingsSelector } from "Bands/state";
import { IDataKeys } from "shared/types";

export const EventSideMenu: FC = () => {
  const { bandId } = useBandContext();
  const { eventId, event, eventBlocks, hasBlocks, userPermissions } = useEventContext();

  const eventInfoBlockTypeSettings = useSelector(
    bandSettingsSelector<IIconBandSetting>(IDataKeys.eventInfoBlockTypes)
  );

  const items: ISideMenuItem[] | undefined = useMemo(
    () =>
      eventBlocks?.map(
        ({ sidemenuId, title, path, icon, actions }) =>
          ({
            id: sidemenuId,
            title,
            path,
            icon,
            actions,
          } as ISideMenuItem)
      ),
    [eventBlocks]
  );

  const addItems: ISideMenuItem[] = useMemo(() => {
    if (!eventId || !bandId) return [];

    const items: ISideMenuItem[] = [];

    if (userPermissions.CREATE_VENUE_BLOCK && !hasBlocks?.[EventUniqueBlocks.venue]) {
      items.push({
        id: "add_venue_block_item",
        title: "Venue",
        type: IMenuItemType.add,
        path: buildUrl(BAND_PATHS.CREATE_VENUE_BLOCK, {
          bandId,
          eventId,
        }),
        icon: Venue,
      });
    }

    if (userPermissions.CREATE_PEOPLE_BLOCK && !hasBlocks?.[EventUniqueBlocks.people]) {
      items.push({
        id: "add_people_block_item",
        title: "People",
        type: IMenuItemType.add,
        path: buildUrl(BAND_PATHS.CREATE_PEOPLE_BLOCK, {
          bandId,
          eventId,
        }),
        icon: UserMultiple,
      } as ISideMenuItem);
    }

    if (userPermissions.CREATE_SCHEDULE_BLOCK) {
      items.push({
        id: "add_schedule_block_item",
        title: "Schedule",
        type: IMenuItemType.add,
        path: buildUrl(BAND_PATHS.CREATE_SCHEDULE_BLOCK, {
          bandId,
          eventId,
        }),
        icon: Clock,
      });
    }

    if (userPermissions.CREATE_GUESTLIST_BLOCK) {
      items.push({
        id: "add_guestlist_block_item",
        title: "Guestlist",
        type: IMenuItemType.add,
        path: buildUrl(BAND_PATHS.CREATE_GUESTLIST_BLOCK, {
          bandId,
          eventId,
        }),
        icon: Guests,
      });
    }

    if (userPermissions.CREATE_TRAVEL_BLOCK) {
      items.push({
        id: "add_travel_block_item",
        title: "Travel",
        type: IMenuItemType.add,
        path: buildUrl(BAND_PATHS.CREATE_TRAVEL_BLOCK, {
          bandId,
          eventId,
        }),
        icon: Plane,
      });
    }

    if (userPermissions.CREATE_ACCOMMODATION_BLOCK) {
      items.push({
        id: "add_accommodation_block_item",
        title: "Accommodation",
        type: IMenuItemType.add,
        path: buildUrl(BAND_PATHS.CREATE_ACCOMMODATION_BLOCK, {
          bandId,
          eventId,
        }),
        icon: Hotel,
      });
    }

    event?.eventInfoBlockTypes
      ?.sort((a, b) => {
        const aIndex = eventInfoBlockTypeSettings.findIndex(
          type => getIdFromPath(type.id, API_ENDPOINTS.EVENT_INFO_BLOCK_TYPES) === a.id
        );
        const bIndex = eventInfoBlockTypeSettings.findIndex(
          type => getIdFromPath(type.id, API_ENDPOINTS.EVENT_INFO_BLOCK_TYPES) === b.id
        );
        return aIndex - bIndex;
      })
      ?.forEach(({ id, label, icon, permissions }) => {
        if (mapUserPermissions(permissions || []).CREATE_BLOCK) {
          items.push({
            id: `add_${kebabCase(label)}_block_item`,
            title: label,
            type: IMenuItemType.add,
            path: buildUrl(BAND_PATHS.CREATE_INFO_BLOCK, {
              bandId,
              eventId,
              blockTypeId: getIdFromPath(id, API_ENDPOINTS.EVENT_INFO_BLOCK_TYPES),
            }),
            icon: getIcon(icon),
          });
        }
      });

    return items;
  }, [
    eventId,
    bandId,
    userPermissions,
    hasBlocks,
    event?.eventInfoBlockTypes,
    eventInfoBlockTypeSettings,
  ]);

  if (!eventId || !bandId) return null;

  return (
    <SideMenu
      type="event"
      title={event?.title || ""}
      titleUrl={buildUrl(BAND_PATHS.BAND_EVENT, { bandId, eventId })}
      items={items}
      addItems={addItems}
      showAddMenu
    />
  );
};
